/**
 * assets/dm-gallery-video.js — play/pause for video slides in the PDP gallery
 * (`snippets/dm-pdp-gallery.liquid`, VIDEO MEDIA). Loaded only on products
 * that have a video, so image-only PDPs carry none of it; kept out of
 * `dm-gallery.js`, which sits just under the AssetSizeJavaScript budget.
 *
 * Only the slide that is actually in view may play. An autoplay slide
 * (`data-dm-gallery-autoplay`, muted loop) starts when it scrolls in; every
 * slide pauses when it scrolls out — `<video>` directly, YouTube / Vimeo
 * embeds by the same postMessage commands Dawn's `pauseAllMedia` sends.
 *
 * Click-to-play slides: Shopify's `video_tag` adds `muted` to any video
 * rendered with `autoplay`, which is how Dawn's `<deferred-media>` starts the
 * clip it clones in on tap — so it started silent. The tap is a user gesture,
 * so the clip is unmuted inside the same click (this listener runs after
 * DeferredMedia's own, which has already inserted the `<video>`).
 *
 * The element sits inside `<dm-gallery>`, so a section re-render (variant
 * change) disconnects the old observer and connects a fresh one.
 */
(function () {
  'use strict';
  if (customElements.get('dm-gallery-video')) return;

  const pause = (slide) => {
    slide.querySelectorAll('video').forEach((v) => { if (!v.paused) v.pause(); });
    slide.querySelectorAll('iframe').forEach((f) => {
      const src = f.getAttribute('src') || '';
      if (!f.contentWindow) return;
      if (src.includes('youtube')) f.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      else if (src.includes('vimeo')) f.contentWindow.postMessage('{"method":"pause"}', '*');
    });
  };

  class DMGalleryVideo extends HTMLElement {
    connectedCallback() {
      const gallery = this.closest('dm-gallery');
      const scroller = gallery && gallery.querySelector('[data-dm-gallery-scroller]');
      const slides = gallery ? gallery.querySelectorAll('.dm-pdp-gallery__slide--video') : [];
      if (!scroller || slides.length === 0) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const slide = entry.target;
          if (entry.intersectionRatio >= 0.6) {
            const video = slide.querySelector('video');
            if (video && slide.hasAttribute('data-dm-gallery-autoplay') && video.paused) {
              const p = video.play();
              if (p && p.catch) p.catch(() => {});
            }
          } else {
            pause(slide);
          }
        });
      }, { root: scroller, threshold: [0, 0.6] });

      this.onTap = (event) => {
        const slide = event.target.closest && event.target.closest('.dm-pdp-gallery__slide--video');
        if (!slide || !event.target.closest('.deferred-media__poster-button')) return;
        const video = slide.querySelector('video');
        if (!video) return;
        video.muted = false;
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
      };
      this.gallery = gallery;
      gallery.addEventListener('click', this.onTap);

      slides.forEach((slide) => {
        /* dm-media-frame renders the muted loop with the `autoplay` attribute.
           Left on, the browser starts it whenever it can — including after a
           pause() issued here — so an off-screen slide kept playing on mobile.
           Playback is this observer's job alone: switch the attribute off and
           pause, then the first callback plays whichever slide is in view. */
        slide.querySelectorAll('video').forEach((v) => {
          v.autoplay = false;
          v.removeAttribute('autoplay');
        });
        pause(slide);
        this.observer.observe(slide);
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.gallery && this.onTap) this.gallery.removeEventListener('click', this.onTap);
    }
  }

  customElements.define('dm-gallery-video', DMGalleryVideo);
})();
